import api from '@/api';

/**
 * Create a numeric representation of a binary string based on permissions set
 * The permissions also needs to be reversed before outputting
 *
 */
const createPermString = (permList) => {
  const permissions = [];

  // Create an array of the permissions
  Object.keys(permList).forEach((priv) => {
    permissions.push(permList[priv] ? 1 : 0);
  });

  // Reverse the array, convert to a string, remove any commas
  const permString = permissions
    .reverse()
    .toString()
    .replace(/,/g, '');
  // Then convert into an actual number
  return parseInt(permString, 2);
};

// TODO: Is there an endpoint for getting all privileges / groups?
const initialUser = {
  name: '',
  email: '',
  ad_user: null,
  password: '',
  bio: null,
  position: null,
  department: null,
  location: null,
  avatar: null,
  avatar_id: null,
  specialism: null,
  privileges: {
    add_edit_pages: false,
    add_edit_news: false,
    publish_pages: false,
    publish_news: false,
    add_policies: false,
    approve_policies: false,
    add_new_policy_categories: false,
    assign_new_roles: false,
    add_edit_interest_groups: false,
    add_edit_events: false,
    publish_events: false,
    add_edit_publish_questions: false,
    add_ideas: false,
    vote_on_ideas: false,
    comment_on_ideas: false,
    respond_to_questions: false,
    delete_questions: false,
    delete_ideas: false,
    delete_comments: false,
    add_idea_statuses: false,
    mark_ideas_for_review: false,
    update_system_statuses: false,
    add_edit_poll: false,
    edit_homepage: false,
    add_files: false,
    add_forms: false,
    approve_forms: false,
    add_new_form_categories: false,
    edit_users: false,
    approve_pages: false,
    add_policy_tag: false,
    edit_policy_tag: false,
    delete_policy_tag: false,
    add_eforms: false,
    approve_eforms: false,
    add_new_eform_categories: false,
    delete_eform_entries: false,
  },
  privilege_groups: {
    user: false,
    collaboration_manager: false,
    policy_manager: false,
    content_editor: false,
    comms_team: false,
    admin: false,
    form_manager: false,
    eform_manager: false,
    indexer: false,
    collaboration_manager_plus: false,
    survey_manager: false,
    poll_manager: false
  },
  superAdmin: false,
};

const initialLink = {
  name: '',
  url: '',
  icon: 'link',
};

export default {
  namespaced: true,

  state: {
    collection: [],
    currentUser: initialUser,
    loading: false,
    filters: {
      searchQuery: '',
      size: 100,
      blocked: false,
    },
    sortBy: {
      field: 'user_id',
      direction: 'asc',
    },
    pagination: {
      total: 10,
      per_page: 10,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 10,
    },
    quickLinks: [],
    currentLink: initialLink,
  },

  mutations: {
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    SET_QUICK_LINKS(state, payload) {
      state.quickLinks = payload;
    },

    SET_CURRENT_LINK(state, payload) {
      state.currentLink = payload;
    },
    setCollection(state, collection) {
      state.collection = collection;
    },

    setCurrentUser(state, currentUser) {
      state.currentUser = currentUser;
    },

    resetUser(state) {
      // deep clone
      state.currentUser = JSON.parse(JSON.stringify(initialUser));
    },
    resetLink(state) {
      state.currentLink = JSON.parse(JSON.stringify(initialLink));
    },
    setSearchQuery(state, val) {
      state.filters.searchQuery = val;
    },
    setBlockedFilter(state, val) {
      state.filters.blocked = val;
    },
    RESET_FILTERS(state) {
      state.filters.searchQuery = '';
      state.filters.blocked = false;
    },
  },

  actions: {
    async listForSelect({ state, commit }, filterTerm) {
      state.loading = true;
      let resp;

      const filter = {
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        query: {
          bool: {
            must: [
              {
                term: {
                  blocked: false,
                },
              },
            ],
          },
        },
      };

      if (filterTerm) {
        filter.query.bool.must.push({
          match_phrase_prefix: {
            name: {
              query: filterTerm,
              slop: 10,
            },
          },
        });
      }

      filter.size = 1000;
      filter.from = 0;

      try {
        resp = await api().post('/users/flat', { filter });
        const users = resp.data.data.user;

        state.loading = false;
        commit('setCollection', users);
        return Promise.resolve(users);
      } catch (error) {
        if (error.response.status === 404 && error.response.data.data.length === 0) {
          state.loading = false;
          commit('setCollection', []);
          return Promise.resolve([]);
        }
        return Promise.reject(error.response.data);
      }
    },

    async list({ commit, state }, payload = { page: 1 }) {
      state.loading = true;
      let resp;

      const filter = {
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        query: {
          bool: {
            must: [
              {
                term: {
                  blocked: state.filters.blocked,
                },
              },
            ],
          },
        },
      };

      if (payload && payload.filter_blocked) {
        filter.query.bool.must = [];
      }

      if (state.filters.searchQuery) {
        filter.query.bool.must.push({
          match_phrase_prefix: {
            name: {
              query: state.filters.searchQuery,
              slop: 10,
            },
          },
        });
      }
      let size;
      let from;
      if (payload && payload.page) {
        size = state.pagination.per_page;
        from = state.pagination.per_page * payload.page - size;
      } else {
        size = 10000;
        from = 0;
      }
      filter.size = size;
      filter.from = from;

      try {
        resp = await api().post('/users/flat', { filter });
        const users = resp.data.data.user;
        if (payload && payload.page) {
          commit('SET_FILTER_TOTAL', resp.data.meta.filter_total);
          commit('SET_CURRENT_PAGE', payload.page);
        }
        commit('setCollection', users);
        state.loading = false;
        return Promise.resolve(users);
      } catch (error) {
        if (error.response.status === 404 && error.response.data.data.length === 0) {
          commit('setCollection', []);
          state.loading = false;
          return Promise.resolve([]);
        }
        return Promise.reject(error.response.data);
      }
    },

    async updateProfile({ commit, state }) {
      let response;

      const toPost = (({
        name, position, department, location, avatar_id, bio, specialism,
      }) => ({
        name,
        position,
        department,
        location,
        avatar_id,
        bio,
        specialism,
      }))(state.currentUser);

      if (state.currentUser.avatar_id) {
        toPost.avatar_id = state.currentUser.avatar_id;
      }

      try {
        response = await api().post(`/user/${state.currentUser.id}`, { user: toPost });
        commit('setCurrentUser', response.data.data.user[0]);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async read({ commit }, { userid, setCurrent }) {
      let response;

      try {
        response = await api().get(`/user/${userid}`);
        if (setCurrent) {
          commit('setCurrentUser', response.data.data.user[0]);
        }
        return Promise.resolve(response.data.data.user[0]);
      } catch (err) {
        if (setCurrent) {
          commit('setCurrentUser', null);
        }
        return Promise.reject(err.response.data);
      }
    },

    getCurrentUser({ commit, dispatch, rootState }) {
      return new Promise((resolve, reject) => {
        if (!rootState.auth.currentUser.id) {
          // need to load the user
          dispatch('auth/listCurrentUser', {}, { root: true })
            .then((resp) => {
              commit('setCurrentUser', resp);
              resolve(resp);
            })
            .catch((err) => {
              console.log(`error getting user - ${err}`);
              reject(err);
            });
        } else {
          commit('setCurrentUser', rootState.auth.currentUser);
          console.log('setCurrentUser', JSON.parse(JSON.stringify(rootState.auth.currentUser)));
          resolve(rootState.auth.currentUser);
        }
      });
    },

    // For the user select, where we can create a user, we just want to add the user in
    quickAdd(context, user) {
      user.bio = '';
      return new Promise((resolve, reject) => {
        api()
          .post('/user', { user })
          .then((resp) => {
            // dispatch('list').then(() => {
            resolve(resp);
            // });
          })
          .catch((err) => {
            reject(err.response.data);
          });
      });
    },

    // Fully fledged add method, based on
    add({ commit, state }, payload = {}) {
      let addPayload;
      // take permissions and convert binary to number
      if (!payload || Object.keys(payload).length === 0) {
        addPayload = { ...state.currentUser };
      } else {
        addPayload = { ...payload };
      }

      // if we are a superAdmin, we need to set the privilege_groups to 0
      if (payload.superAdmin) {
        addPayload.privilege_groups = 0;
      } else {
        addPayload.privilege_groups = createPermString(addPayload.privilege_groups);
      }

      addPayload.bio = '';

      // set privileges to null for now
      addPayload.privileges = null;

      return new Promise((resolve, reject) => {
        api()
          .post('/user', { user: addPayload })
          .then((resp) => {
            commit('resetUser');
            // dispatch('list').then(() => {
            resolve(resp);
            // });
          })
          .catch((err) => {
            reject(err.response.data);
          });
      });
    },

    async updateUser({ state }) {
      // so we need to create binary stuff here
      const payload = { ...state.currentUser };

      // if we are a superAdmin, we need to set the privilege_groups to 0
      if (payload.superAdmin) {
        payload.privilege_groups = 0;
      } else {
        payload.privilege_groups = createPermString(payload.privilege_groups);
      }

      // set privileges to null for now
      payload.privileges = null;

      // need to change interestgroup ids to be just ids, if they exist
      if (payload.interestgroups) {
        payload.interestgroup_ids = payload.interestgroups.map(grp => grp.id);
        delete payload.interestgroups;
      }

      let response;
      try {
        response = await api().post(`/user/${state.currentUser.id}`, {
          user: payload,
        });
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async delete(context, userId) {
      let resp;

      try {
        resp = await api().delete(`/user/${userId}`);
        return Promise.resolve(resp);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    blockUser(context, userId) {
      return new Promise((resolve, reject) => {
        api()
          .post(`/user/${userId}/block`)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            reject(err.response.data);
          });
      });
    },

    async unblockUser(context, userId) {
      let response;

      try {
        response = await api().post(`/user/${userId}/unblock`);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error.response.data);
      }
    },

    async getQuickLinks({ commit }, forceUpdate = false) {
      let response;
      let linkObject;

      // updating this to now integrate with localStorage
      const linkCheck = localStorage.getItem('mft_users_getQuickLinks');

      const filter = {
        sort: { priority: "DESC" }
      }

      if (!linkCheck || forceUpdate) {
        try {
          response = await api().post('/currentuser/quicklinks', { filter });
          const sortedLinks = response.data.data.quicklink.sort((a,b) => {
            return b.priority - a.priority
          })
          linkObject = {
            items: sortedLinks,
            timestamp: new Date().getTime(),
          };
          
          localStorage.setItem('mft_users_getQuickLinks', JSON.stringify(linkObject));
        } catch (err) {
          linkObject = {
            items: [],
            timestamp: new Date().getTime(),
          };
          localStorage.setItem('mft_users_getQuickLinks', JSON.stringify(linkObject));
        }
      } else {
        linkObject = JSON.parse(linkCheck);
      }
      commit('SET_QUICK_LINKS', linkObject.items || []);
      return Promise.resolve(linkObject.items || []);
    },

    async getQuickLink({ commit }, id) {
      let response;

      try {
        response = await api().get(`/currentuser/quicklink/${id}`);
        commit('SET_CURRENT_LINK', response.data.data.quicklink[0]);
        return Promise.resolve(response.data.data.quicklink[0]);
      } catch (err) {
        commit('SET_CURRENT_LINK', initialLink);
        return Promise.resolve([]);
      }
    },

    resetLink({ commit }) {
      commit('resetLink');
      return Promise.resolve();
    },

    async addQuickLink({ dispatch }, link) {
      let response;

      console.log(link);

      try {
        response = await api().post('/currentuser/quicklink', { quicklink: link });
        dispatch('getQuickLinks', true);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async editLink({ dispatch }, { payload, id }) {
      let response;

      const toPost = (({ name, url, icon, priority }) => ({
        name,
        url,
        icon,
        priority
      }))(payload);

      try {
        response = await api().post(`/currentuser/quicklink/${id}`, { quicklink: toPost });
        dispatch('getQuickLinks', true);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    removeQuickLink({ dispatch }, id) {
      try {
        api()
          .delete(`/currentuser/quicklink/${id}`)
          .then(() => {
            console.log('success');
            dispatch('getQuickLinks', true);
          })
          .catch((err) => {
            console.log('error', err);
            this.success = false;
          });

        return Promise.resolve();
      } catch (err) {
        return Promise.reject(err);
      }
    },
  },

  getters: {
    activeUsers: state => state.collection.filter(user => user.blocked === 0),
    blockedUsers: state => state.collection.filter(user => user.blocked === 1),
  },
};
